export default function storageApis($apiInstance, $store) {
    return {
        // 파일 목록 조회
        getFiles: async (folder = '', params = {}, container = '') => {
            $store.commit('setIsLoading', true);
            
            try {
                // 요청 헤더 및 파라미터 설정
                const queryParams = new URLSearchParams();
                
                if (params.pageSize) {
                    queryParams.append('pageSize', params.pageSize);
                }
                
                if (params.continuationToken) {
                    queryParams.append('continuationToken', params.continuationToken);
                }
                
                // folder가 있는 경우 prefix로 추가
                if (folder && folder !== '/') {
                    queryParams.append('prefix', folder);
                }
                
                // 컨테이너 추가
                if (container) {
                    queryParams.append('container', container);
                }
                
                // 컨테이너 헤더 설정
                const config = {
                    headers: {
                        'X-Azure-Container': container || 'certilife'  // 기본값 설정
                    }
                };
                
                // API URL 구성
                const apiUrl = `/object-storages/azure/files?${queryParams.toString()}`;
                console.log(`파일 목록 조회 요청: ${apiUrl}`, config);
                
                // API 호출
                const response = await $apiInstance.get(apiUrl, config);
                
                // 응답 로깅
                console.log('파일 목록 조회 응답:', response.data);
                
                // 응답 데이터 검증 및 변환
                if (response.data && response.data.success) {
                    const responseData = response.data.data || {};
                    
                    // 새로운 응답 구조 검증
                    if (!responseData.items) {
                        console.warn('응답에 items 배열이 없습니다. 빈 배열로 초기화합니다.');
                        responseData.items = [];
                    }
                    
                    // 경로 정보 검증
                    if (!responseData.currentPath) {
                        responseData.currentPath = folder || '/';
                    }
                    
                    // breadcrumbs 검증
                    if (!Array.isArray(responseData.breadcrumbs)) {
                        responseData.breadcrumbs = [{ name: 'Root', path: '/' }];
                    }
                    
                    return {
                        success: true,
                        data: responseData
                    };
                }
                
                // 실패 응답 처리
                return {
                    success: false,
                    error: response.data.message || '파일 목록 조회에 실패했습니다.',
                    data: {
                        items: [],
                        currentPath: folder || '/',
                        parentPath: null,
                        breadcrumbs: [{ name: 'Root', path: '/' }],
                        continuationToken: null,
                        currentContainer: container || 'certilife'
                    }
                };
            } catch (error) {
                console.error('파일 목록 조회 중 오류 발생:', error);
                
                return {
                    success: false,
                    error: error.message || '파일 목록을 불러오는데 실패했습니다.',
                    status: error.response?.status,
                    data: {
                        items: [],
                        currentPath: folder || '/',
                        parentPath: null,
                        breadcrumbs: [{ name: 'Root', path: '/' }],
                        continuationToken: null,
                        currentContainer: container || 'certilife'
                    }
                };
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 폴더 생성
        createFolder: async (folderPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.post(`/object-storages/azure/folders/${folderPath}`);
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 폴더 삭제
        deleteFolder: async (folderPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.delete(`/object-storages/azure/folders/${folderPath}`);
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 파일 업로드
        uploadFiles: async (formData, folderPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                // formData가 이미 FormData 객체인 경우 그대로 사용
                let data = formData;
                
                // 이전 방식 호환성 유지 (files 배열이 전달되는 경우)
                if (Array.isArray(formData)) {
                    data = new FormData();
                    formData.forEach((file, index) => {
                        if (file instanceof File) {
                            console.log(`배열에서 파일 #${index + 1} 추가:`, file.name || '이름 없음');
                            data.append('files', file);
                        } else {
                            console.error(`배열에서 파일 #${index + 1}이 File 객체가 아님:`, file);
                        }
                    });
                }
                
                // FormData가 아닌 경우 오류 처리
                if (!(data instanceof FormData)) {
                    throw new Error('업로드 데이터가 유효하지 않습니다.');
                }
                
                // FormData 내용 상세 검증
                const files = data.getAll('files');
                console.log('FormData의 files 항목 수:', files.length);
                
                if (!files || files.length === 0) {
                    throw new Error('업로드할 파일이 없습니다.');
                }
                
                // 파일 객체 타입 및 속성 검사
                let validFileCount = 0;
                files.forEach((file, index) => {
                    // 유효한 File 객체인지 확인
                    const isValidFile = file instanceof File;
                    
                    console.log(`FormData에서 파일 #${index + 1} 검증:`, {
                        name: file.name || '이름 없음',
                        type: file.type || '타입 없음',
                        size: file.size || '크기 없음',
                        isFile: isValidFile,
                        constructor: file.constructor ? file.constructor.name : 'Constructor 없음'
                    });
                    
                    if (isValidFile) {
                        validFileCount++;
                    }
                    
                    // 필수 속성 검사
                    if (!file.name) {
                        console.warn(`파일 #${index + 1}에 이름이 없습니다.`);
                    }
                    if (!file.type) {
                        console.warn(`파일 #${index + 1}에 타입이 없습니다.`);
                    }
                    if (!file.size) {
                        console.warn(`파일 #${index + 1}에 크기가 없습니다.`);
                    }
                });
                
                if (validFileCount === 0) {
                    throw new Error('유효한 파일 객체가 없습니다.');
                }
                
                // 클라이언트에서 검증하지만 서버에서도 다시 확인
                const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
                const invalidFiles = files.filter(file => {
                    if (!file.name) return true; // 이름 없으면 유효하지 않음
                    const extension = file.name.split('.').pop().toLowerCase();
                    return !allowedExtensions.includes(extension);
                });
                
                if (invalidFiles.length > 0) {
                    throw new Error(`지원되지 않는 파일 형식이 포함되어 있습니다: ${invalidFiles.map(f => f.name || '이름 없음').join(', ')}`);
                }
                
                // folderPath 정리
                let cleanFolderPath = '';
                if (folderPath && typeof folderPath === 'string') {
                    cleanFolderPath = folderPath.trim();
                    
                    // 특수문자 검사
                    const invalidChars = /[<>:"|?*]/g;
                    if (invalidChars.test(cleanFolderPath)) {
                        throw new Error('폴더 경로에 허용되지 않는 특수문자가 포함되어 있습니다.');
                    }
                    
                    // 경로 정규화 (끝에 슬래시가 없는 경우 추가)
                    if (cleanFolderPath.length > 0 && !cleanFolderPath.endsWith('/')) {
                        cleanFolderPath += '/';
                    }
                    
                    // 앞에 슬래시가 있는 경우 제거
                    if (cleanFolderPath.startsWith('/')) {
                        cleanFolderPath = cleanFolderPath.substring(1);
                    }
                    
                    // 경로가 복잡한 경우 기본 경로로 테스트
                    if (cleanFolderPath.split('/').length > 2) {
                        console.warn('복잡한 경로가 감지되었습니다. 서버에서 처리하지 못할 수 있습니다.');
                    }
                }
                
                // URL 생성
                let url = '/object-storages/azure/files/upload';
                
                // 파라미터 구성
                const queryParams = [];
                
                // 폴더 경로 파라미터 추가
                if (cleanFolderPath) {
                    queryParams.push(`folderPath=${encodeURIComponent(cleanFolderPath)}`);
                }
                
                // 컨테이너 파라미터 추가 (FormData에서 확인)
                const containerParam = data.get('container');
                if (containerParam) {
                    const containerName = containerParam.toString().trim();
                    if (containerName) {
                        console.log('컨테이너 지정:', containerName);
                        queryParams.push(`container=${encodeURIComponent(containerName)}`);
                    }
                }
                
                // 쿼리 파라미터가 있으면 URL에 추가
                if (queryParams.length > 0) {
                    url += `?${queryParams.join('&')}`;
                }
                
                console.log('업로드 URL:', url);
                console.log('파일 목록:', files.map(f => `${f.name || '이름 없음'} (${f.size || '크기 없음'} bytes, ${f.type || '타입 없음'})`));
                console.log('FormData 키 목록:', [...data.keys()]);

                // 업로드 요청 설정 (타임아웃 설정 포함)
                const uploadOptions = {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    // 타임아웃 설정 - 큰 파일 업로드를 위해 연장
                    timeout: 120000, // 120초 타임아웃
                    validateStatus: function (status) {
                        console.log('응답 상태 코드:', status);
                        return status >= 200 && status < 300; // 기본 동작
                    },
                    // 업로드 진행 상황 추적
                    onUploadProgress: function(progressEvent) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log('업로드 진행률:', percentCompleted + '%');
                    }
                };
                
                // 컨테이너 헤더 추가 (서버에서 지원하는 경우)
                if (containerParam) {
                    uploadOptions.headers['X-Azure-Container'] = containerParam.toString().trim();
                }
                
                // Azure 서버가 제대로 받았는지 확인하기 위한 로깅
                console.log('[Azure 업로드 시작] 요청 URL:', url);
                console.log('[Azure 업로드 시작] Content-Type:', uploadOptions.headers['Content-Type']);
                console.log('[Azure 업로드 시작] 파일 수:', files.length);
                
                try {
                    const response = await $apiInstance.post(url, data, uploadOptions);
                    console.log('업로드 응답:', response.data);
                    return {
                        success: true,
                        data: response.data
                    };
                } catch (uploadError) {
                    console.error('[Azure 업로드 실패]', uploadError);
                    
                    // 실패 시 단일 파일로 다시 시도
                    if (files.length > 1) {
                        console.log('여러 파일 업로드 실패. 단일 파일로 다시 시도합니다.');
                        
                        // 새 FormData 생성
                        const singleFileData = new FormData();
                        singleFileData.append('files', files[0]);
                        
                        try {
                            const singleResponse = await $apiInstance.post(url, singleFileData, {
                                ...uploadOptions,
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                }
                            });
                            
                            console.log('단일 파일 업로드 성공:', singleResponse.data);
                            message.warning('단일 파일만 업로드되었습니다. 다중 파일 업로드에 문제가 있습니다.');
                            return {
                                success: true,
                                data: singleResponse.data
                            };
                        } catch (singleError) {
                            console.error('단일 파일 업로드도 실패:', singleError);
                            throw uploadError; // 원래 오류 유지
                        }
                    } else {
                        throw uploadError;
                    }
                }
            } catch (error) {
                console.error('File upload error:', error);
                
                // 상세 오류 정보 출력
                if (error.response) {
                    console.error('Error response:', {
                        data: error.response.data,
                        status: error.response.status,
                        headers: error.response.headers
                    });
                    
                    // 서버 오류 메시지 분석
                    if (error.response.data && error.response.data.message) {
                        const serverMessage = error.response.data.message;
                        console.error('서버 오류 메시지:', serverMessage);
                        
                        // 일반적인 Azure Storage 오류 처리
                        if (serverMessage.includes('The requested URI does not represent any resource')) {
                            console.error('Azure URI 오류: 존재하지 않는 컨테이너 또는 경로');
                            error.friendlyMessage = 'Azure Storage 경로에 문제가 있습니다. 관리자에게 문의하세요.';
                        } else if (serverMessage.includes('AuthenticationFailed')) {
                            console.error('Azure 인증 실패');
                            error.friendlyMessage = 'Azure Storage 연결 권한이 없습니다. 관리자에게 문의하세요.';
                        } else if (serverMessage.includes('length')) {
                            console.error('Azure 파일 크기 제한 초과');
                            error.friendlyMessage = '파일 크기가 너무 큽니다. 더 작은 파일을 사용해주세요.';
                        }
                    }
                } else if (error.request) {
                    console.error('Error request:', error.request);
                    error.friendlyMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인하세요.';
                } else {
                    console.error('Error message:', error.message);
                    error.friendlyMessage = '파일 업로드 중 오류가 발생했습니다.';
                }
                
                return {
                    success: false,
                    error: error.friendlyMessage || '파일 업로드 중 오류가 발생했습니다.',
                    status: error.response?.status
                };
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 파일 삭제
        deleteFile: async (blobName, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.delete(`/object-storages/azure/files/${blobName}`);
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 파일 이동
        moveFile: async (blobName, destinationPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.post(`/object-storages/azure/files/${blobName}/move`, {
                    destinationPath
                });
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 파일 복사
        copyFile: async (blobName, destinationPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.post(`/object-storages/azure/files/${blobName}/copy`, {
                    destinationPath
                });
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 파일 이름 변경
        renameFile: async (blobName, newPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                const response = await $apiInstance.patch(`/object-storages/azure/files/${blobName}/rename`, {
                    newPath
                });
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 폴더 이름 변경
        renameFolder: async (folderPath, newPath, success, fail) => {
            $store.commit('setIsLoading', true);

            try {
                // 경로 정규화: 앞뒤 슬래시 제거하여 중복 슬래시 방지
                const normalizedPath = folderPath.replace(/^\/+|\/+$/g, '');
                console.log(`폴더 이름 변경 요청 - 원본 경로: "${folderPath}", 정규화된 경로: "${normalizedPath}"`);
                
                const response = await $apiInstance.patch(`/object-storages/azure/folders/${normalizedPath}/rename`, {
                    newPath
                });
                if (response.data.success) {
                    success(response);
                } else {
                    fail({ response: { data: response.data } });
                }
            } catch (error) {
                console.error('폴더 이름 변경 오류:', error);
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 스토리지 상태 확인
        checkStorageStatus: async () => {
            try {
                const response = await $apiInstance.get('/object-storages/azure/status');
                return response.data;
            } catch (error) {
                console.error('스토리지 상태 확인 실패:', error);
                throw error;
            }
        },

        // 파일 및 폴더 검색
        searchFiles: async (keyword, params = {}, container = '') => {
            $store.commit('setIsLoading', true);
            
            try {
                // 요청 파라미터 설정
                const queryParams = new URLSearchParams();
                
                // 필수 검색어 추가
                queryParams.append('keyword', keyword);
                
                // 선택적 파라미터 추가
                if (params.type) {
                    queryParams.append('type', params.type);
                }
                
                if (params.pageSize) {
                    queryParams.append('pageSize', params.pageSize);
                }
                
                if (params.continuationToken) {
                    queryParams.append('continuationToken', params.continuationToken);
                }
                
                // 컨테이너 추가
                if (container) {
                    queryParams.append('container', container);
                }
                
                // 컨테이너 헤더 설정
                const config = {
                    headers: {
                        'X-Azure-Container': container || 'certilife'  // 기본값 설정
                    }
                };
                
                // API URL 구성
                const apiUrl = `/object-storages/azure/search?${queryParams.toString()}`;
                console.log(`파일 검색 요청: ${apiUrl}`, config);
                
                // API 호출
                const response = await $apiInstance.get(apiUrl, config);
                
                // 응답 로깅
                console.log('파일 검색 응답:', response.data);
                
                // 응답 데이터 검증 및 변환
                if (response.data && response.data.success) {
                    const responseData = response.data.data || {};
                    
                    // 검색 결과 검증
                    if (!responseData.items) {
                        console.warn('응답에 items 배열이 없습니다. 빈 배열로 초기화합니다.');
                        responseData.items = [];
                    }
                    
                    return {
                        success: true,
                        data: responseData
                    };
                }
                
                // 실패 응답 처리
                return {
                    success: false,
                    error: response.data.message || '파일 검색에 실패했습니다.',
                    data: {
                        items: [],
                        currentPath: '/',
                        parentPath: null,
                        breadcrumbs: [{ name: 'Root', path: '/' }],
                        continuationToken: null,
                        currentContainer: container || 'certilife'
                    }
                };
            } catch (error) {
                console.error('파일 검색 중 오류 발생:', error);
                
                return {
                    success: false,
                    error: error.message || '파일 검색에 실패했습니다.',
                    status: error.response?.status,
                    data: {
                        items: [],
                        currentPath: '/',
                        parentPath: null,
                        breadcrumbs: [{ name: 'Root', path: '/' }],
                        continuationToken: null,
                        currentContainer: container || 'certilife'
                    }
                };
            } finally {
                $store.commit('setIsLoading', false);
            }
        }
    }
}
